import * as React from 'react';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { LoginForm } from '../Login';
import { useAuth } from '../../../context/Auth.context';
import { isFollowing, followProvider, unfollowProvider } from '../../../utils/follows';

/**
 * Follow the OPERATOR, not the listing.
 *
 * A lodge with four listings should not need following four times, and the
 * thing a traveller means by "follow" is the people, not the room type. The
 * button sits beside the operator's name for that reason.
 *
 * SIGNED OUT, IT PROMPTS RATHER THAN HIDES. A button that vanishes for
 * anonymous visitors teaches nobody that the feature exists, and this app's
 * travellers are anonymous by default. Clicking opens the same login dialog
 * the header uses; the follow is not applied afterwards, deliberately — the
 * traveller came back to a page mid-task and gets to choose again rather than
 * discovering an action was taken while they were typing a password.
 *
 * Optimistic, and reverted on failure: the write is one small document and
 * waiting on a round trip to fill in a button makes the whole page feel slow.
 */
export function FollowButton({ provider }) {
  const { user } = useAuth();
  const [following, setFollowing] = React.useState(false);
  const [openLogin, setOpenLogin] = React.useState(false);
  const [notice, setNotice] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const providerId = provider?.id;
  const uid = user?.uid;

  React.useEffect(() => {
    let live = true;
    if (!uid || !providerId) {
      setFollowing(false);
      return undefined;
    }

    isFollowing(uid, providerId).then((next) => {
      if (live) setFollowing(next);
    });

    return () => { live = false; };
  }, [uid, providerId]);

  if (!providerId || !provider?.name) return null;

  const toggle = async () => {
    if (!uid) {
      setOpenLogin(true);
      return;
    }

    const next = !following;
    setFollowing(next);
    setBusy(true);

    const ok = next
      ? await followProvider(uid, provider)
      : await unfollowProvider(uid, providerId);

    setBusy(false);

    if (!ok) {
      // Say so rather than leaving a button that claims something untrue.
      setFollowing(!next);
      setNotice('That did not save. Check your connection and try again.');
      return;
    }

    setNotice(next ? `Following ${provider.name}.` : `Unfollowed ${provider.name}.`);
  };

  return (
    <>
      <Button
        onClick={toggle}
        disabled={busy}
        size="small"
        variant={following ? 'contained' : 'outlined'}
        startIcon={following ? <CheckRoundedIcon /> : <AddRoundedIcon />}
        aria-pressed={following}
        sx={{ borderRadius: 999, flexShrink: 0 }}
      >
        {following ? 'Following' : 'Follow'}
      </Button>

      <LoginForm open={openLogin} setOpen={setOpenLogin} />

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      </Snackbar>
    </>
  );
}

export default FollowButton;
